import React, { Fragment, useState } from 'react';
import { Flex, Box, Button, Heading, Text } from 'rebass';
import { withWizard } from 'react-albus';
import { compose, withApollo } from 'react-apollo';
import { withToastManager } from 'react-toast-notifications';
import styled from 'styled-components';
import { Icon } from '@iconify/react';
import camera from '@iconify/icons-entypo/camera';
import check from '@iconify/icons-entypo/check';
import Dropzone from 'react-dropzone';
import { BoxButton } from '../../components/async/SharedComponents';
import { isMobile } from 'react-device-detect';
import PhotoTaker from '../../components/asyncIntake/PhotoTaker';
import SpinLoader from '../../components/SpinLoader';
import { sleep } from '../../utils/sleep';

const Title = styled(Text)`
  font-weight: 600;
  font-size: 20px;
  line-height: 27px;
`;

const Description = styled(Text)`
  font-weight: 300;
  font-size: 14px;
  line-height: 21px;
`;

const FakeLink = styled(Text)`
  font-weight: 600;
  font-size: 14px;
  line-height: 21px;
  text-align: center;
  text-decoration-line: underline;
  cursor: pointer;
  color: #44a5ff;
`;

const FakeButton = styled(Flex)`
  cursor: pointer;
  height: 48px;
  background: #ffffff;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.15);
  border-radius: 10px;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  line-height: 21px;
  text-align: center;
  color: #354e79;
  width: 124px;
`;

const CameraIconBox = styled(Flex)`
  width: 68px;
  height: 68px;
  background: #e7f0ff;
  border-radius: 10px;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const UploadStatusTag = styled(Box)`
  border-radius: 10px;
  padding: 2px 8px;
  margin-left: 12px;
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  display: flex;
  justify-content: center;
  align-items: center;
  &.success {
    background: #e7f0ff;
    color: #354e79;
  }
  &.failed {
    background: #ffd3cb;
    color: #ff0f00;
  }
`;

const EmptyStateFlexWrapper = styled(Flex)`
  cursor: pointer;
  &:focus {
    outline: 0 !important;
    box-shadow: none !important;
  }
  &:focus:not(.focus-visible) {
    outline: 0 !important;
    box-shadow: none !important;
  }
`;

const PictureMiniature = ({ file, onFileRemove, onConfirm, isSelfie }) => {
  // console.log(file);
  return (
    <Flex flex={1} flexDirection="column" style={{ zIndex: 1 }}>
      <Box mt="0" mb={3}>
        <Heading textAlign="left">Review and Confirm</Heading>
      </Box>

      <Flex
        flexDirection="row"
        flexWrap="wrap"
        style={{
          width: 'calc(100vw / 2)',
          // height: 'calc(100vw / 2)',
          maxWidth: '600px',
          // maxHeight: '600px',
          borderRadius: '20px',
          justifyContent: 'center',
          alignItems: 'center',
          margin: '0 auto',
          position: 'relative',
        }}
      >
        <img
          src={(file || {}).data}
          alt="No Preview Available"
          width="100%"
          style={{
            maxHeight: '300px',
            borderRadius: '20px',
            objectFit: 'contain',
          }}
        />
        <Button
          onClick={onFileRemove}
          variant="white"
          style={{ position: 'absolute', bottom: '6px', right: '6px' }}
        >
          Change
        </Button>
      </Flex>

      <Box mt={3} mb={3}>
        <Title textAlign="left">Ensure that...</Title>
      </Box>
      <Description>
        <span>
          <Icon
            icon={check}
            style={{
              color: 'rgb(53, 78, 121)',
              fontSize: '18px',
              alignSelf: 'center',
            }}
          />
        </span>{' '}
        The photo is not blurry or dark
      </Description>

      <Description>
        <span>
          <Icon
            icon={check}
            style={{
              color: 'rgb(53, 78, 121)',
              fontSize: '18px',
              alignSelf: 'center',
            }}
          />
        </span>{' '}
        The photo has not been edited/filtered
      </Description>
      <Description>
        <span>
          <Icon
            icon={check}
            style={{
              color: 'rgb(53, 78, 121)',
              fontSize: '18px',
              alignSelf: 'center',
            }}
          />
        </span>{' '}
        We can read clearly the text, name, numbers
      </Description>
      <Description>
        <span>
          <Icon
            icon={check}
            style={{
              color: 'rgb(53, 78, 121)',
              fontSize: '18px',
              alignSelf: 'center',
            }}
          />
        </span>{' '}
        Your fingers are not covering part of the card
      </Description>

      <Button
        onClick={onConfirm}
        variant="primary"
        mt={4}
        style={{
          marginLeft: '12px',
          marginRight: '12px',
        }}
      >
        Confirm
      </Button>
    </Flex>
  );
};

const EmptyState = ({
  onDropAccepted,
  title,
  photo,
  Description,
  takeDesktopPhoto = () => {},
  onDeletePhoto = () => {},
  uploadStatus,
  toastManager,
}) => {
  return (
    <Dropzone
      noClick={false}
      multiple={false}
      onDropRejected={() => {
        toastManager.add(
          'Invalid file: Make sure your file is a pdf or a jpeg or a png and less than 10mb',
          {
            appearance: 'error',
            autoDismissTimeout: 10000,
          }
        );
      }}
      maxSize={10000000}
      onDropAccepted={onDropAccepted}
      style={{
        marginBottom: 36,
      }}
    >
      {({ getRootProps, getInputProps, isDragActive, open }) => (
        <EmptyStateFlexWrapper
          flexDirection="column"
          {...getRootProps()}
          onClick={() => {
            open();
          }}
        >
          <Flex flexDirection="row">
            <Flex flexDirection="column" flex={1} pr="12px">
              <Flex flexDirection="row">
                <Title>{title}</Title>
                {uploadStatus ? (
                  uploadStatus === 'Uploading' ? (
                    <SpinLoader
                      style={{ marginLeft: '12px' }}
                      width={15}
                      height={15}
                    />
                  ) : (
                    <UploadStatusTag
                      className={
                        uploadStatus === 'Uploaded' ? 'success' : 'failed'
                      }
                    >
                      {uploadStatus}
                    </UploadStatusTag>
                  )
                ) : null}
              </Flex>
              {Description}
              {isMobile ? (
                <FakeButton>+ Take Photo</FakeButton>
              ) : (
                <Flex flexDirection="row">
                  <FakeButton>Upload Photo</FakeButton>
                  <FakeButton
                    ml="16px"
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      takeDesktopPhoto();
                    }}
                  >
                    Take Photo
                  </FakeButton>
                </Flex>
              )}
            </Flex>
            <CameraIconBox
              onClick={e => {
                if (!photo) {
                  if (!isMobile) {
                    e.stopPropagation();
                    e.preventDefault();
                    takeDesktopPhoto();
                  }
                } else {
                  e.preventDefault();
                  e.stopPropagation();
                  onDeletePhoto();
                }
              }}
            >
              {!photo ? (
                <Icon
                  icon={camera}
                  style={{
                    color: 'rgb(53, 78, 121)',
                    fontSize: '24px',
                    alignSelf: 'center',
                  }}
                />
              ) : (
                <>
                  <svg
                    style={{
                      position: 'absolute',
                      top: '-11px',
                      right: '-11px',
                      cursor: 'pointer',
                    }}
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.34 8.3218C15.4361 8.23224 15.5135 8.12464 15.568 8.00513C15.6225 7.88563 15.6529 7.75656 15.6575 7.62531C15.6621 7.49405 15.6408 7.36318 15.5948 7.24015C15.5488 7.11713 15.4791 7.00437 15.3895 6.90831C15.2999 6.81224 15.1923 6.73476 15.0728 6.68029C14.9533 6.62581 14.8243 6.59541 14.693 6.59081C14.5617 6.58621 14.4309 6.60751 14.3078 6.6535C14.1848 6.69948 14.0721 6.76924 13.976 6.85881L11.05 9.58681L8.32199 6.65981C8.13946 6.47285 7.891 6.36476 7.62979 6.35867C7.36857 6.35258 7.11535 6.44897 6.9243 6.62721C6.73325 6.80545 6.61955 7.05139 6.60753 7.3124C6.59552 7.57341 6.68613 7.82876 6.85999 8.02381L9.58799 10.9498L6.66099 13.6778C6.56154 13.7665 6.4808 13.8741 6.42353 13.9944C6.36625 14.1147 6.33359 14.2452 6.32746 14.3783C6.32133 14.5114 6.34186 14.6444 6.38784 14.7694C6.43382 14.8944 6.50432 15.009 6.59521 15.1065C6.6861 15.2039 6.79554 15.2822 6.9171 15.3367C7.03866 15.3912 7.16989 15.4209 7.30309 15.424C7.43628 15.4271 7.56876 15.4036 7.69273 15.3548C7.8167 15.306 7.92966 15.2329 8.02499 15.1398L10.951 12.4128L13.679 15.3388C13.7671 15.4401 13.8747 15.5226 13.9953 15.5814C14.116 15.6403 14.2472 15.6742 14.3813 15.6812C14.5153 15.6883 14.6494 15.6682 14.7756 15.6223C14.9017 15.5764 15.0173 15.5056 15.1155 15.414C15.2137 15.3225 15.2924 15.2121 15.347 15.0895C15.4017 14.9669 15.431 14.8345 15.4334 14.7003C15.4358 14.5661 15.4111 14.4328 15.3609 14.3083C15.3107 14.1839 15.2359 14.0708 15.141 13.9758L12.414 11.0498L15.34 8.3218Z"
                      fill="#354E79"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0 11C0 4.925 4.925 0 11 0C17.075 0 22 4.925 22 11C22 17.075 17.075 22 11 22C4.925 22 0 17.075 0 11ZM11 20C9.8181 20 8.64778 19.7672 7.55585 19.3149C6.46392 18.8626 5.47177 18.1997 4.63604 17.364C3.80031 16.5282 3.13738 15.5361 2.68508 14.4442C2.23279 13.3522 2 12.1819 2 11C2 9.8181 2.23279 8.64778 2.68508 7.55585C3.13738 6.46392 3.80031 5.47177 4.63604 4.63604C5.47177 3.80031 6.46392 3.13738 7.55585 2.68508C8.64778 2.23279 9.8181 2 11 2C13.3869 2 15.6761 2.94821 17.364 4.63604C19.0518 6.32387 20 8.61305 20 11C20 13.3869 19.0518 15.6761 17.364 17.364C15.6761 19.0518 13.3869 20 11 20Z"
                      fill="#354E79"
                    />
                  </svg>

                  <img
                    src={photo}
                    alt=""
                    width="68px"
                    height="68px"
                    style={{ objectFit: 'cover' }}
                  />
                </>
              )}
            </CameraIconBox>
          </Flex>

          <input type="file" {...getInputProps()} />
        </EmptyStateFlexWrapper>
      )}
    </Dropzone>
  );
};

const onDesktopPhotoCapture = async (dta, callback) => {
  // console.log({dta})
  var reader = new FileReader();
  reader.readAsDataURL(dta);
  reader.onloadend = async () => {
    var base64data = reader.result;

    const fls = {
      fileName: 'userId.jpg',
      mimeType: 'image/jpeg',
      data: base64data,
    };

    callback(fls);
  };
};

const fileAccepted = (f, callback) => {
  for (let index = 0; index < f.length; index++) {
    const element = f[index];

    const reader = new FileReader();
    reader.onload = event => {
      let fls = f;
      fls = {
        fileName: element.name,
        mimeType: element.type,
        data: event.target.result,
      };

      callback(fls);
    };
    reader.readAsDataURL(element);
  }
};

const UploadInsuranceCard = ({ onSubmit, toastManager }) => {
  // console.log('UPLOAD INS CARD!!!');
  const [file, setFile] = useState();
  const [fileDL, setFileDL] = useState();
  const [processing, setProcessing] = useState(false);
  const [isSelfieTaking, setIsSelfieTaking] = useState(false);
  const [isDlTaking, setIsDlTaking] = useState(false);
  const [isSelfieConfirming, setIsSelfieConfirming] = useState(false);
  const [isDlConfirming, setIsDlConfirming] = useState(false);
  const [photoUploadStatus, setPhotoUploadStatus] = useState();
  const [dlUploadStatus, setDlUploadStatus] = useState();

  if (isSelfieTaking) {
    return (
      <Flex
        flex={1}
        flexDirection="column"
        style={{ zIndex: 100, alignItems: 'center', justifyContent: 'center', top:0, left: 0, bottom: 0, right:0, position: 'absolute', background:'#fdf5f1' }}
      >
        <PhotoTaker
          onCapture={f =>
            onDesktopPhotoCapture(f, ff => {
              setFile(ff);
              setIsSelfieConfirming(true);
              setIsSelfieTaking(false);
            })
          }
        />
      </Flex>
    );
  }

  if (isDlTaking) {
    return (
      <Flex
        flex={1}
        flexDirection="column"
        style={{ zIndex: 100, alignItems: 'center', justifyContent: 'center', top:0, left: 0, bottom: 0, right:0, position: 'absolute', background:'#fdf5f1' }}
      >
        <PhotoTaker
          onCapture={f =>
            onDesktopPhotoCapture(f, ff => {
              setFileDL(ff);
              setIsDlConfirming(true);
              setIsDlTaking(false);
            })
          }
        />
      </Flex>
    );
  }

  const DescriptionSelfie = (
    <Description mb="16px" mt="16px">
      Please upload a picture of the front side of your insurance card
    </Description>
  );

  const DescriptionDL = (
    <Description mb="16px" mt="16px">
      Please upload a picture of the back side of your insurance card
    </Description>
  );

  return (
    <Fragment>
      {!isSelfieConfirming && !isDlConfirming ? (
        <Flex
          flex={1}
          flexDirection="column"
          style={{ zIndex: 0, maxWidth: '500px' }}
        >
          <Box mt="0" mb={3}>
            <Title textAlign="left">Insurance Information</Title>
          </Box>

          <Box mt="0" mb={4}>
            <Description>
              Please upload photos of both sides of your insurance card so we
              can start checking your coverage.
            </Description>
          </Box>

          <EmptyState
            title="Front"
            Description={DescriptionSelfie}
            photo={(file || {}).data}
            uploadStatus={photoUploadStatus}
            onDeletePhoto={() => {
              setFile(null);
            }}
            onDropAccepted={f => {
              fileAccepted(f, ff => {
                setPhotoUploadStatus('');
                setFile(ff);
                setIsSelfieConfirming(true);
                window.scrollTo({ top: 0 });
              });
            }}
            toastManager={toastManager}
            takeDesktopPhoto={async() => {
              setFile(null);
              setPhotoUploadStatus('');
              setIsSelfieTaking(true);
              window.scrollTo({ top: 0 });
            }}
          />
          <Box mt={4} height="1px" />
          <EmptyState
            title="Back"
            Description={DescriptionDL}
            uploadStatus={dlUploadStatus}
            photo={(fileDL || {}).data}
            toastManager={toastManager}
            onDeletePhoto={() => {
              setFileDL(null);
            }}
            onDropAccepted={f => {
              fileAccepted(f, ff => {
                setDlUploadStatus('');
                setFileDL(ff);
                setIsDlConfirming(true);
                window.scrollTo({ top: 0 });
              });
            }}
            takeDesktopPhoto={() => {
              setFileDL(null);
              setDlUploadStatus('');
              setIsDlTaking(true);
              window.scrollTo({ top: 0 });
            }}
          />

          <Flex flexDirection="row" mt={4}>
            <svg
              width="31"
              height="32"
              viewBox="0 0 31 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M23.353 32.001H6.832C6.66374 32.0011 6.4971 31.9681 6.34163 31.9038C6.18615 31.8394 6.04488 31.7451 5.9259 31.6261C5.80692 31.5071 5.71257 31.3659 5.64824 31.2104C5.58391 31.0549 5.55087 30.8883 5.551 30.72V18.496C5.551 17.788 6.124 17.21 6.832 17.21H8.384V15.236C8.384 11.538 11.394 8.528 15.092 8.528C18.79 8.528 21.8 11.538 21.8 15.236V17.21H23.352C24.06 17.21 24.633 17.788 24.633 18.496V30.72C24.6331 30.8883 24.6001 31.0549 24.5358 31.2104C24.4714 31.3659 24.3771 31.5071 24.2581 31.6261C24.1391 31.7451 23.9979 31.8394 23.8424 31.9038C23.6869 31.9681 23.5203 32.0011 23.352 32.001H23.353ZM15.932 25.241C17.453 24.382 16.843 22.059 15.093 22.059C13.348 22.064 12.739 24.377 14.254 25.241V26.897C14.254 28.012 15.931 28.012 15.931 26.897L15.932 25.241ZM12.234 17.209H17.958V15.235C17.958 13.657 16.672 12.376 15.093 12.376C13.514 12.376 12.234 13.657 12.234 15.235V17.209ZM5.156 15.392H1.172C0.521 15.392 0 14.866 0 14.22C0 13.574 0.521 13.048 1.172 13.048H5.156C5.802 13.048 6.328 13.574 6.328 14.22C6.328 14.866 5.802 15.392 5.156 15.392ZM7.963 9.121C7.69196 9.12151 7.42961 9.02543 7.223 8.85L4.067 6.256C3.94833 6.15841 3.85011 6.03832 3.778 5.90265C3.70589 5.76698 3.66131 5.61839 3.64681 5.46543C3.63232 5.31247 3.64819 5.15815 3.69353 5.01135C3.73887 4.86454 3.81277 4.72815 3.911 4.61C4.00824 4.49096 4.12805 4.39232 4.26354 4.31976C4.39904 4.24719 4.54754 4.20213 4.70052 4.18717C4.85349 4.17221 5.00791 4.18764 5.1549 4.23257C5.30189 4.27751 5.43854 4.35106 5.557 4.449L8.713 7.043C9.562 7.741 9.062 9.121 7.963 9.116V9.121ZM15.093 6.36C14.7823 6.35947 14.4845 6.23582 14.2649 6.01614C14.0452 5.79647 13.9215 5.49867 13.921 5.188V1.172C13.921 0.526 14.447 0 15.093 0C15.739 0 16.265 0.526 16.265 1.172V5.188C16.265 5.834 15.739 6.36 15.093 6.36ZM22.223 9.121C21.119 9.121 20.624 7.741 21.473 7.043L24.629 4.449C25.837 3.459 27.322 5.267 26.119 6.262L22.963 8.856C22.7548 9.02812 22.4931 9.1222 22.223 9.122V9.121ZM29.015 15.392H24.994C24.343 15.392 23.822 14.866 23.822 14.22C23.822 13.574 24.343 13.048 24.994 13.048H29.015C29.666 13.048 30.187 13.574 30.187 14.22C30.187 14.866 29.666 15.392 29.015 15.392Z"
                fill="#F6A293"
              />
            </svg>

            <Description
              flex="1"
              ml="12px"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              All photos are encrypted and accessible only to authorized
              clinical staff.
            </Description>
          </Flex>

          <Button
            variant="primary"
            disabled={!(file && file.data && fileDL && fileDL.data) || !!processing}
            style={{
              marginLeft: '12px',
              marginRight: '12px',
              alignSelf: 'center',
            }}
            width={[1, 1 / 2]}
            mt={4}
            type="button"
            onClick={() => onSubmit(file, fileDL)}
          >
            Continue
          </Button>
        </Flex>
      ) : isSelfieConfirming ? (
        <Flex
          flex={1}
          flexDirection="column"
          style={{ zIndex: 100, alignItems: 'center', justifyContent: 'center', top:0, left: 0, bottom: 0, right:0, position: 'absolute', background:'#fdf5f1' }}
        >
          <PictureMiniature
            file={file}
            onConfirm={async() => {
              setIsSelfieConfirming(false);
              await sleep(200);
              window.scrollTo({ top: document.body.scrollHeight });
              // uploadPhoto();
            }}
            isSelfie={true}
            onFileRemove={async() => {
              setIsSelfieConfirming(false);
              setFile(null);
              await sleep(200);
              window.scrollTo({ top: document.body.scrollHeight });
            }}
          />
        </Flex>
      ) : (
        <Flex
          flex={1}
          flexDirection="column"
          style={{ zIndex: 100, alignItems: 'center', justifyContent: 'center', top:0, left: 0, bottom: 0, right:0, position: 'absolute', background:'#fdf5f1' }}
        >
          <PictureMiniature
            file={fileDL}
            isSelfie={false}
            onConfirm={async() => {
              setIsDlConfirming(false);
              await sleep(200);
              window.scrollTo({ top: document.body.scrollHeight });
              // uploadDl();
            }}
            onFileRemove={async() => {
              setIsDlConfirming(false);
              setFileDL(null);
              await sleep(200);
              window.scrollTo({ top: document.body.scrollHeight });
            }}
          />
        </Flex>
      )}
    </Fragment>
  );
};

export default compose(withToastManager)(UploadInsuranceCard);
